import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Tables } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { PermAction, PermOptions, permOptionsKey, TableWithPermissions } from '@/types/base/permissions'
import Authuser, { authCanPerform, evaluatePermissions } from '@/types/base/authuser'
import { getWorkspaceById } from './workspaces'

const logger = createIsomorphicLogger('db.collections')

export const getCollectionById = async (supabase: SupabaseClient<Database>, collectionId: string) => {
  const { data: collection, error } = await supabase
    .from("collections")
    .select("*")
    .eq("id", collectionId)
    .single()

  if (!collection) {
    logError(logger, error, 'Failed to fetch Collection by ID', { collectionId })
    throw new Error(error.message)
  }

  return collection
}

export const getOptionalCollectionById = async (supabase: SupabaseClient<Database>, collectionId: string) => {
  const { data: collection, error } = await supabase
    .from("collections")
    .select("*")
    .eq("id", collectionId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Collection by ID', { collectionId })
    throw new Error(error.message)
  }

  return collection
}

export const getOptionalCollectionByName = async (supabase: SupabaseClient<Database>, collectionName: string,
		workspaceId: string) => {
  const { data, error } = await supabase
    .from("workspaces")
    .select(
      `
      collections (*)
    `
    )
    .eq("id", workspaceId)
    .eq("collections.name", collectionName)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Collection by name', { collectionName, workspaceId })
    throw new Error(error.message)
  }

  return data ? data.collections[0] : null
}

export const getCollectionWorkspacesByWorkspaceId = async (
	supabase: SupabaseClient<Database>,
  workspaceId: string
) => {
  const { data: workspace, error } = await supabase
    .from("workspaces")
    .select(
      `
      id,
      name,
      collections (*)
    `
    )
    .eq("id", workspaceId)
    .single()

  if (!workspace) {
    logError(logger, error, 'Failed to fetch Collection workspaces by workspace ID', { workspaceId })
    throw new Error(error.message)
  }

  return workspace.collections ?? []
}

export const getCollectionsWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'collections'>[]> => {
	const wsCollections = await getCollectionWorkspacesByWorkspaceId(supabase, workspaceId)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'collections'>[] = []

	for (const [i, collection] of wsCollections.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
			{ type: "collections", context: "ANY", uuid: collection.id, tenant:collection.owning_tenant, ws: workspace})

		if (i === 0) { // Attach special non specific permission to first record only
			const options = { type: "collections", action: PermAction.create, context: "aidonAdmin" } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const shareKey = permOptionsKey(options)
			perms[shareKey] = allowed
		}

		result.push({ ...collection, permissions: perms })
	}

	return result
}

export const getCollectionWorkspacesByCollectionId = async (
	supabase: SupabaseClient<Database>,
  collectionId: string
) => {
  const { data: collection, error } = await supabase
    .from("collections")
    .select(
      `
      id,
      name,
      workspaces (*)
    `
    )
    .eq("id", collectionId)
    .single()

  if (!collection) {
    logError(logger, error, 'Failed to fetch Collection workspaces by collection ID', { collectionId })
    throw new Error(error.message)
  }

  return collection
}

export const getAutoShareCollections = async (supabase: SupabaseClient<Database>) => {
  const { data: collections, error } = await supabase
    .from("collections")
    .select("*")
    .eq("auto_share", true)

  if (error) {
    logError(logger, error, 'Failed to fetch auto-share Collections')
    throw new Error(error.message)
  }

  return collections
}

export const createCollection = async (
	supabase: SupabaseClient<Database>,
  collection: TablesInsert<"collections">,
  workspace: Tables<"workspaces">
) => {
	const collectionWithTenant = {
		...collection,
		owning_tenant: workspace.tenant_id
	}

  const { data: createdCollection, error } = await supabase
    .from("collections")
    .insert([collectionWithTenant])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Collection', { collection: printObject(collection) })
    throw new Error(error.message)
  }

  await createCollectionWorkspace(supabase, {
    user_id: createdCollection.user_id,
    collection_id: createdCollection.id,
    workspace_id: workspace.id
  })

  logger.info('Collection created successfully', { createdCollection: printObject(createdCollection) })
  return createdCollection
}

export const createCollections = async (
	supabase: SupabaseClient<Database>,
  collections: TablesInsert<"collections">[],
  workspace: Tables<"workspaces">
) => {
	const collectionsWithTenant = collections.map(collection => ({
		...collection,
		owning_tenant: workspace.tenant_id
	}))

  const { data: createdCollections, error } = await supabase
    .from("collections")
    .insert(collectionsWithTenant)
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create multiple Collections', { collections: printObject(collections) })
    throw new Error(error.message)
  }

  await createCollectionWorkspaces(supabase,
    createdCollections.map(collection => ({
      user_id: collection.user_id,
      collection_id: collection.id,
	    workspace_id: workspace.id
    }))
  )

  logger.info('Collections created successfully', { createdCollections: printObject(createdCollections) })
  return createdCollections
}

export const createCollectionWorkspace = async (supabase: SupabaseClient<Database>, item: {
  user_id: string
  collection_id: string
  workspace_id: string
}) => {
  const { data: createdCollectionWorkspace, error } = await supabase
    .from("collection_workspaces")
    .insert([item])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Collection workspace', { item: printObject(item) })
    throw new Error(error.message)
  }

  logger.info('Collection workspace created successfully', { createdCollectionWorkspace: printObject(createdCollectionWorkspace) })
  return createdCollectionWorkspace
}

export const createCollectionWorkspaces = async (
	supabase: SupabaseClient<Database>,
  items: { user_id: string; collection_id: string; workspace_id: string }[]
) => {
  const { data: createdCollectionWorkspaces, error } = await supabase
    .from("collection_workspaces")
    .upsert(items, { onConflict: 'collection_id, workspace_id' })
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Collection workspaces', { items: printObject(items) })
    throw new Error(error.message)
  }

  logger.info('Collection workspaces created successfully', { createdCollectionWorkspaces: printObject(createdCollectionWorkspaces) })
  return createdCollectionWorkspaces
}

export const updateCollection = async (
	supabase: SupabaseClient<Database>,
  collectionId: string,
  collection: TablesUpdate<"collections">
) => {
  const { data: updatedCollection, error } = await supabase
    .from("collections")
    .update(collection)
    .eq("id", collectionId)
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to update Collection', { collectionId, collection: printObject(collection) })
    throw new Error(error.message)
  }

  return updatedCollection
}

export const deleteCollection = async (supabase: SupabaseClient<Database>, collectionId: string) => {
  const { error } = await supabase
    .from("collections")
    .delete()
    .eq("id", collectionId)

  if (error) {
    logError(logger, error, 'Failed to delete Collection', { collectionId })
    throw new Error(error.message)
  }

  return true
}

export const deleteCollectionWorkspace = async (
	supabase: SupabaseClient<Database>,
  collectionId: string,
  workspaceId: string
) => {
  const { error } = await supabase
    .from("collection_workspaces")
    .delete()
    .eq("collection_id", collectionId)
    .eq("workspace_id", workspaceId)

  if (error) {
    logError(logger, error, 'Failed to delete Collection workspace', { collectionId })
    throw new Error(error.message)
  }
  return true
}
